import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Users, FileText, Loader2, AlertCircle } from "lucide-react";

interface AttributedSentence {
  sentence_id: number;
  text: string;
  speaker: string;
  confidence: number;
  flags: string[];
  char_start: number;
  char_end: number;
}

interface AttributionResult {
  sentences: AttributedSentence[];
  reconstructedTranscript: string;
  summary: {
    totalSentences: number;
    speakerTotals: Record<string, number>;
    escalationRate: number;
    needsHumanReviewCount: number;
  };
}

interface SpeakerAttributedTranscriptProps {
  transcription: string;
  attributionStatus?: "processing" | "done" | "failed";
  attributionJson?: string;
  autoAcceptThreshold?: number;
  onTriggerAttribution?: () => void;
  initialMode?: "raw" | "labeled";
}

const SPEAKER_COLORS: Record<string, { label: string; badge: string; bubble: string }> = {
  SPEAKER_A: {
    label: "Clinician",
    badge: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800",
    bubble: "bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-400 dark:border-blue-600",
  },
  SPEAKER_B: {
    label: "Patient",
    badge: "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800",
    bubble: "bg-green-50 dark:bg-green-950/30 border-l-4 border-green-400 dark:border-green-600",
  },
  AMBIGUOUS: {
    label: "Unknown",
    badge: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
    bubble: "bg-amber-50 dark:bg-amber-950/30 border-l-4 border-amber-400 dark:border-amber-600",
  },
};

function getSpeakerStyle(speaker: string) {
  return SPEAKER_COLORS[speaker] ?? {
    label: speaker,
    badge: "bg-muted text-muted-foreground border-border",
    bubble: "bg-muted/30 border-l-4 border-muted-foreground/40",
  };
}

interface Turn {
  speaker: string;
  sentences: AttributedSentence[];
}

function buildTurns(sentences: AttributedSentence[]): Turn[] {
  const sorted = [...sentences].sort((a, b) => a.char_start - b.char_start);
  const turns: Turn[] = [];
  let current: Turn | null = null;
  for (const s of sorted) {
    if (!current || current.speaker !== s.speaker) {
      current = { speaker: s.speaker, sentences: [s] };
      turns.push(current);
    } else {
      current.sentences.push(s);
    }
  }
  return turns;
}

export function SpeakerAttributedTranscript({
  transcription,
  attributionStatus,
  attributionJson,
  autoAcceptThreshold = 85,
  onTriggerAttribution,
  initialMode = "raw",
}: SpeakerAttributedTranscriptProps) {
  const [mode, setMode] = useState<"raw" | "labeled">(initialMode);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  const attribution: AttributionResult | null = (() => {
    if (!attributionJson) return null;
    try {
      return JSON.parse(attributionJson);
    } catch {
      return null;
    }
  })();

  const hasAttribution = attributionStatus === "done" && attribution !== null;
  const isProcessing = attributionStatus === "processing";
  const hasFailed = attributionStatus === "failed";

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {hasAttribution && (
            <div className="flex items-center rounded-lg border border-border overflow-hidden">
              <Button
                variant="ghost"
                size="sm"
                className={`h-7 px-3 rounded-none text-xs ${mode === "raw" ? "bg-muted text-foreground" : "text-muted-foreground"}`}
                onClick={() => setMode("raw")}
                data-testid="button-transcript-mode-raw"
              >
                <FileText className="w-3 h-3 mr-1.5" />
                Raw
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`h-7 px-3 rounded-none text-xs ${mode === "labeled" ? "bg-muted text-foreground" : "text-muted-foreground"}`}
                onClick={() => setMode("labeled")}
                data-testid="button-transcript-mode-labeled"
              >
                <Users className="w-3 h-3 mr-1.5" />
                Speaker Labels
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isProcessing && (
            <span
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
              data-testid="status-attribution-processing"
            >
              <Loader2 className="w-3 h-3 animate-spin" />
              Labeling speakers…
            </span>
          )}
          {hasFailed && (
            <span
              className="inline-flex items-center gap-1.5 text-xs text-destructive"
              data-testid="status-attribution-failed"
            >
              <AlertCircle className="w-3 h-3" />
              Speaker labeling failed
              {onTriggerAttribution && (
                <button
                  className="underline ml-1"
                  onClick={onTriggerAttribution}
                  data-testid="button-retry-attribution"
                >
                  Retry
                </button>
              )}
            </span>
          )}
          {hasAttribution && attribution && (
            <span className="text-xs text-muted-foreground" data-testid="status-attribution-done">
              {attribution.summary.needsHumanReviewCount > 0 && (
                <span className="text-amber-600 dark:text-amber-400 mr-2">
                  {attribution.summary.needsHumanReviewCount} uncertain
                </span>
              )}
              {Object.entries(attribution.summary.speakerTotals)
                .map(([spk, n]) => `${getSpeakerStyle(spk).label}: ${n}`)
                .join(" · ")}
            </span>
          )}
        </div>
      </div>

      {mode === "raw" || !hasAttribution ? (
        <div className="prose prose-sm dark:prose-invert max-w-none" data-testid="text-transcription">
          <p className="whitespace-pre-wrap text-foreground leading-relaxed">{transcription}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="text-transcription-labeled">
          {buildTurns(attribution.sentences).map((turn, turnIdx) => {
            const style = getSpeakerStyle(turn.speaker);
            return (
              <div
                key={turnIdx}
                className={`pl-3 py-2 pr-3 rounded-r-md ${style.bubble}`}
                data-testid={`speaker-turn-${turnIdx}`}
              >
                <div className="mb-1">
                  <span
                    className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full border ${style.badge}`}
                    data-testid={`speaker-label-${turnIdx}`}
                  >
                    {style.label}
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">
                  {turn.sentences.map((s, sIdx) => {
                    const uncertain = s.confidence < autoAcceptThreshold;
                    return (
                      <span key={sIdx}>
                        {sIdx > 0 ? " " : ""}
                        <span
                          className={uncertain ? "opacity-70" : ""}
                          title={uncertain ? `Confidence: ${s.confidence}% — may be uncertain` : undefined}
                          data-testid={uncertain ? `uncertain-sentence-${s.sentence_id}` : undefined}
                        >
                          {s.text}
                          {uncertain && (
                            <span className="text-amber-500 dark:text-amber-400 ml-0.5 text-xs">[?]</span>
                          )}
                        </span>
                      </span>
                    );
                  })}
                </p>
              </div>
            );
          })}
          {attribution.summary.needsHumanReviewCount > 0 && (
            <p className="text-xs text-muted-foreground mt-2" data-testid="text-attribution-legend">
              <span className="text-amber-500 dark:text-amber-400">[?]</span> marks sentences below the auto-accept confidence threshold
            </p>
          )}
        </div>
      )}
    </div>
  );
}
